import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Play, Pause, Rewind, FastForward, X, Music, Loader2, ChevronUp } from 'lucide-react';
import { usePlayer, formatTime } from '../../contexts/PlayerContext';
import useCoverPool from '../../hooks/useCoverPool';
import { useResolvedPortrait } from '../../hooks/useSpeakerPortrait';

export const MiniPlayer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    current, isPlaying, buffering, currentTime, duration, progress, queue,
    togglePlay, skip, seekToPercent, closePlayer,
  } = usePlayer();
  const barRef = useRef(null);
  const touchSeekRef = useRef(null);
  const onPlayerPage = location.pathname.startsWith('/player/');

  // Toggle body class so pages can reserve bottom padding for the mini player.
  useEffect(() => {
    const active = !!current && !onPlayerPage;
    document.body.classList.toggle('has-mini-player', active);
    return () => { document.body.classList.remove('has-mini-player'); };
  }, [current, onPlayerPage]);

  // Keep the player page URL in sync when auto-next advances the track.
  // CRITICAL: forward the live context queue via router state — otherwise
  // PlayerPage sees no queue and clobbers it with a same-speaker fallback,
  // breaking topic/parsha/holiday queues after the first skip.
  useEffect(() => {
    if (!current || !onPlayerPage) return;
    const urlId = location.pathname.split('/player/')[1];
    if (urlId && urlId !== current.drive_file_id) {
      navigate(`/player/${current.drive_file_id}`, { replace: true, state: { queue } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.drive_file_id]);

  // Cover resolution — Feb-2026 (user bug): the mini-player used to just
  // read `current.cover_image` as-is, which on mobile meant it could
  // show a stale/wrong picture (typically a translucent cutout URL that
  // was baked into the recording's cover_image at index time). Now we
  // mirror the AudioPlayer's priority: pool > raw non-cutout > speaker
  // portrait fallback, so BOTH desktop and mobile see the same curated
  // pool image (from the `speaker_cover_images` Drive folder 1bSVs…).
  // Hooks MUST run every render — declared BEFORE the early-return so
  // React can track them stably.
  const { pickCover } = useCoverPool();
  const portraitFallback = useResolvedPortrait(current);

  // Feb-2026 (user): iOS occasionally swallows the click event on the
  // 15-sec skip buttons. Wire a `pointerup` fallback (touch/pen only)
  // and dedupe both paths within 400 ms so a real tap never doubles.
  // `useRef` survives renders — MiniPlayer re-renders 4×/sec from the
  // currentTime tick, so a plain `{ current: 0 }` would be re-created
  // every render and lose the dedup window if a render lands between
  // pointerup and the synthetic click. (Caught in iter_114 review.)
  const lastSkipTs = useRef(0);

  if (!current || onPlayerPage) return null;

  const skipDedup = (seconds) => {
    const now = Date.now();
    if (now - lastSkipTs.current < 400) return;
    lastSkipTs.current = now;
    skip(seconds);
  };
  const bindSkip = (seconds) => ({
    onClick: (e) => { e.preventDefault(); e.stopPropagation(); skipDedup(seconds); },
    onPointerUp: (e) => {
      if (e.pointerType === 'mouse') return;
      e.stopPropagation();
      skipDedup(seconds);
    },
  });

  const handleSeekClick = (e) => {
    const rect = barRef.current.getBoundingClientRect();
    const pct = ((e.clientX - rect.left) / rect.width) * 100;
    seekToPercent(Math.max(0, Math.min(100, pct)));
  };

  const isCutoutUrl = (u) => typeof u === 'string'
    && (u.includes('/api/portrait/cutout/') || /portraits-cutout(-\d+)?\//.test(u));
  const rawCover = current.cover_image && typeof current.cover_image === 'string'
    && current.cover_image.startsWith('http')
    ? current.cover_image
    : null;
  const pooledCover = pickCover(current.speaker || '', current.drive_file_id || '');
  const coverSrc = pooledCover
    || (rawCover && !isCutoutUrl(rawCover) ? rawCover : null)
    || (portraitFallback && !isCutoutUrl(portraitFallback) ? portraitFallback : null);
  const hasCover = !!coverSrc;

  return (
    <div
      className="fixed inset-x-0 z-50 bottom-0 sm:pb-0"
      style={{
        paddingBottom: 'env(safe-area-inset-bottom, 0)',
        // Feb 2026 (user "the audio player that follows you must match
        // the bottom bar perfectly and look great together as they are
        // right on top of another"):
        //
        // The MiniPlayer and MobileBottomNav are stacked, so their
        // gradients CONTINUE across the seam. MiniPlayer runs 0.55 →
        // 0.72 alpha; the nav below picks up at 0.72 and runs to 0.90.
        // The result is a single continuous vertical gradient from a
        // light frosted teal at the top of the mini-player, down to a
        // deep teal at the very bottom of the nav — no color break,
        // no double gold line, no seam.
        background: 'linear-gradient(180deg, rgba(3,39,58,0.55) 0%, rgba(4,42,61,0.72) 100%)',
        WebkitBackdropFilter: 'blur(26px) saturate(220%) contrast(110%)',
        backdropFilter: 'blur(26px) saturate(220%) contrast(110%)',
        // Gold top border matches the nav's gold top border below. On
        // desktop no nav exists below, so the top border also frames
        // the stand-alone mini-player nicely.
        borderTop: '1px solid rgba(230,200,100,0.40)',
        // Softer upward shadow — lifts the stack off the page without
        // creating a dark seam between MiniPlayer and nav below.
        boxShadow: '0 -8px 24px -6px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.08)',
      }}
      data-testid="mini-player"
    >
      {/* Seek strip — big enough to tap but small enough NOT to eat
          horizontal-swipe gestures on the carousels above.
          Feb-2026 v2 (user "when i scroll to right on continue
          listening or the by parsha or coming up very often on
          mobile it opens up the next bottom bar which i dont want to
          happen when one is scrolling"): the old `h-10` (40 px)
          hitbox sat as an invisible strip above the visible bar and
          silently caught fingers that drifted down while
          horizontal-scrolling the home rows, triggering a seek and
          "opening" the MiniPlayer. Shrunk to `h-3` on mobile (still
          12 px tall — plenty for a tap on a real thumb) so
          carousel swipes no longer collide with it. Desktop keeps
          its 10 px strip. `touch-none` disables the browser's own
          gesture on the strip so scrubbing doesn't accidentally
          scroll the page. */}
      <div
        ref={barRef}
        className="absolute bottom-full left-0 right-0 h-3 sm:h-[10px] cursor-pointer group flex items-end touch-none"
        onClick={handleSeekClick}
        onTouchStart={(e) => {
          // Jun-2026: do NOT seek on touchstart. The first finger of a
          // pinch arrives as a single touch, so seeking here jumped
          // playback whenever the user pinch-zoomed over the strip.
          // We only remember the position and commit on move/end.
          if (e.touches.length > 1) { touchSeekRef.current = null; return; }
          const t = e.touches[0]; if (!t) return;
          touchSeekRef.current = { x: t.clientX, multi: false };
        }}
        onTouchMove={(e) => {
          if (e.touches.length > 1) { touchSeekRef.current = { x: 0, multi: true }; return; }
          if (touchSeekRef.current?.multi) return;
          const t = e.touches[0]; if (!t) return;
          // Prevent the browser from turning the drag into a page scroll
          // — without this, iOS steals half the drag and the seek jumps.
          if (e.cancelable) e.preventDefault();
          touchSeekRef.current = { x: t.clientX, multi: false };
          const rect = barRef.current.getBoundingClientRect();
          const pct = ((t.clientX - rect.left) / rect.width) * 100;
          seekToPercent(Math.max(0, Math.min(100, pct)));
        }}
        onTouchEnd={(e) => {
          const st = touchSeekRef.current;
          touchSeekRef.current = null;
          if (!st || st.multi) return;
          if (e.touches.length > 0) return;          // still mid-pinch
          const t = e.changedTouches[0]; if (!t) return;
          const rect = barRef.current.getBoundingClientRect();
          const pct = ((t.clientX - rect.left) / rect.width) * 100;
          seekToPercent(Math.max(0, Math.min(100, pct)));
        }}
        onTouchCancel={() => { touchSeekRef.current = null; }}
        data-testid="mini-seek"
      >
        <div className="w-full h-1.5 sm:h-[3px] group-hover:h-[6px] bg-white/15 transition-all">
          <div className="h-full bg-[#c9a43a]" style={{ width: `${progress}%` }} />
        </div>
      </div>

      <div className="flex items-center gap-3 px-3 sm:px-5 h-16">
        {/* Track info — click to expand */}
        <button
          type="button"
          className="flex items-center gap-3 min-w-0 flex-1 sm:flex-initial sm:w-72 text-left group"
          onClick={() => navigate(`/player/${current.drive_file_id}`)}
          data-testid="mini-expand"
        >
          <div className="w-11 h-11 rounded-md bg-[#1f2b47] flex items-center justify-center overflow-hidden flex-shrink-0">
            {hasCover ? (
              <img loading="lazy" decoding="async" src={coverSrc} alt={current.speaker} className="w-full h-full object-cover" />
            ) : (
              <Music className="w-5 h-5 text-[#9a7b24]" />
            )}
          </div>
          <div className="min-w-0">
            <p className="text-white text-sm font-medium truncate group-hover:text-[#e8b04a] transition-colors" data-testid="mini-title">
              {current.title}
            </p>
            <p className="text-white/50 text-xs truncate">{current.speaker}</p>
          </div>
          <ChevronUp className="w-4 h-4 text-white/40 group-hover:text-white flex-shrink-0 hidden sm:block transition-colors" />
        </button>

        {/* Controls — Feb 05 pm: back to slim Rewind / FastForward
            icons with a compact "15" label so users see the direction
            (▶︎ / ◀︎) instantly, no more ambiguity. */}
        <div className="flex items-center gap-1 sm:gap-2 mx-auto">
          <button
            type="button"
            {...bindSkip(-15)}
            style={{ touchAction: 'manipulation' }}
            className="flex flex-col items-center justify-center w-9 h-9 rounded-full text-[#e8b04a] hover:text-white hover:bg-white/10 transition-colors"
            aria-label="Back 15 seconds"
            data-testid="mini-skip-back"
          >
            <Rewind className="w-4 h-4" fill="currentColor" strokeWidth={0} />
            <span className="text-[8px] font-extrabold leading-none">15</span>
          </button>
          <button
            type="button"
            onClick={togglePlay}
            className="w-10 h-10 rounded-full bg-[#9a7b24] hover:bg-[#7d6119] text-white flex items-center justify-center shadow-md transition-colors"
            aria-label={isPlaying ? 'Pause' : 'Play'}
            data-testid="mini-play-toggle"
          >
            {buffering ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : isPlaying ? (
              <Pause className="w-5 h-5" />
            ) : (
              <Play className="w-5 h-5 ml-0.5" />
            )}
          </button>
          <button
            type="button"
            {...bindSkip(15)}
            style={{ touchAction: 'manipulation' }}
            className="flex flex-col items-center justify-center w-9 h-9 rounded-full text-[#e8b04a] hover:text-white hover:bg-white/10 transition-colors"
            aria-label="Forward 15 seconds"
            data-testid="mini-skip-fwd"
          >
            <FastForward className="w-4 h-4" fill="currentColor" strokeWidth={0} />
            <span className="text-[8px] font-extrabold leading-none">15</span>
          </button>
        </div>

        {/* Right cluster — Feb 05 pm: user asked to drop the speed
            pill from the mini bar. Speed is set on the main player /
            recordings page; the following-bar just plays. */}
        <div className="flex items-center gap-2 sm:gap-3">
          <span className="hidden md:block text-white/50 text-xs tabular-nums whitespace-nowrap" data-testid="mini-time">
            {formatTime(currentTime)} / {formatTime(duration)}
          </span>
          <button
            type="button"
            onClick={closePlayer}
            className="p-2 text-white/50 hover:text-white transition-colors"
            aria-label="Close player"
            data-testid="mini-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};
